import { ArrayLikeUtils } from './ArrayLikeUtils';
import {
  buildInsertionOps,
  buildSubPaneIdentityMap,
  executeExitMarkInsertions,
  findExitingMarkIndices,
  groupExitingMarksBySubPane,
  readPageIntervalTable,
  writeBackIntervalTable,
} from './DataStorePaneStateUtility';
import { IDataStore } from './IDataStore';

export function injectExitMarksIntoBase(
  fromDS: IDataStore,
  toDS: IDataStore,
  exitingMarkIdsByPane?: Map<string, Set<number>> | null
): Map<string, Set<number>> {
  const result = new Map<string, Set<number>>();

  const paneIds = toDS.getAllKeys('PDMarks');
  for (const paneId of paneIds) {
    const exitingToIndices = injectExitMarksForPane(paneId, fromDS, toDS, exitingMarkIdsByPane?.get(paneId) ?? null);
    if (exitingToIndices && exitingToIndices.size > 0) {
      result.set(paneId, exitingToIndices);
    }
  }

  return result;
}

function injectExitMarksForPane(
  paneId: string,
  fromDS: IDataStore,
  toDS: IDataStore,
  exitingMarkIds?: Set<number> | null
): Set<number> | null {
  const encodingsPath = `PDMarks.${paneId}.Encodings`;
  const fromMarkIds = ArrayLikeUtils.toArray(fromDS.getValue(`${encodingsPath}.MarkId`));
  const toMarkIds = ArrayLikeUtils.toArray(toDS.getValue(`${encodingsPath}.MarkId`));
  if (fromMarkIds.length === 0 || toMarkIds.length === 0) {
    return null;
  }

  const exitingFromIndices =
    exitingMarkIds == null
      ? findExitingMarkIndices(fromMarkIds, toMarkIds)
      : fromMarkIds.reduce<number[]>((indices, markId, index) => {
          if (exitingMarkIds.has(markId) && !toMarkIds.includes(markId)) {
            indices.push(index);
          }
          return indices;
        }, []);
  if (exitingFromIndices.length === 0) {
    return null;
  }

  const toTable = readPageIntervalTable(toDS);
  const fromTable = readPageIntervalTable(fromDS);
  if (!toTable || !fromTable) {
    return null;
  }

  const toSubPaneToRow = buildSubPaneIdentityMap(paneId, toTable);
  const exitsBySubPane = groupExitingMarksBySubPane(paneId, exitingFromIndices, fromTable);
  if (exitsBySubPane.size === 0) {
    return null;
  }

  const insertions = buildInsertionOps(exitsBySubPane, toSubPaneToRow, toTable.end);
  if (insertions.length === 0) {
    return null;
  }
  insertions.sort((a, b) => a.insertAt - b.insertAt);

  const exitingToIndices = executeExitMarkInsertions(insertions, encodingsPath, fromDS, toDS, toTable, paneId);
  writeBackIntervalTable(toDS, toTable);

  return exitingToIndices.size > 0 ? exitingToIndices : null;
}
